import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Coins, Swords, ArrowLeft, Search, Trophy } from "lucide-react";
import { PageHeader, Loader, Empty, btn } from "@/components/Page";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import Modal from "@/components/Modal";

export default function RecruitFighters() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fighters, setFighters] = useState([]);
  const [circuit, setCircuit] = useState(null);
  const [selectedFighter, setSelectedFighter] = useState(null);
  const [offerModal, setOfferModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Offer form
  const [numFights, setNumFights] = useState(3);
  const [paymentPerFight, setPaymentPerFight] = useState(10);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [circuitRes, fightersRes] = await Promise.all([
        api.get("/arena/circuits/my-circuit"),
        api.get("/arena/fighters/browse")
      ]);

      if (!circuitRes.data.circuit) {
        navigate("/arena-circuit/create-circuit");
        return;
      }

      setCircuit(circuitRes.data.circuit);

      // Filter out fighters already in roster
      const roster = circuitRes.data.circuit.roster || [];
      const availableFighters = fightersRes.data.fighters.filter(
        f => !roster.includes(f.id)
      );

      setFighters(availableFighters);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  const openOfferModal = (fighter) => {
    setSelectedFighter(fighter);
    setNumFights(3);
    setPaymentPerFight(10);
    setOfferModal(true);
  };

  const handleCreateOffer = async () => {
    if (!selectedFighter) return;

    const totalPayment = numFights * paymentPerFight;

    setBusy(true);
    try {
      const { data } = await api.post("/arena/contracts/offer", {
        fighter_id: selectedFighter.id,
        num_fights: numFights,
        payment_per_fight: paymentPerFight
      });

      toast.success(data.message, { 
        description: `${totalPayment} gold escrowed`,
        icon: <Coins size={16} /> 
      });
      setOfferModal(false);
      setSelectedFighter(null);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loader />;

  if (!circuit) return null;

  const totalPayment = numFights * paymentPerFight;
  const filteredFighters = fighters.filter(f => 
    f.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/arena-circuit/my-circuit")}
          className={btn() + " bg-zinc-800 hover:bg-zinc-700"}
        >
          <ArrowLeft size={16} />
        </button>
        <PageHeader
          icon={UserPlus}
          title="Recruit Gladiators"
          subtitle="Browse free agents and offer contracts"
        />
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search gladiators by name..."
          className="w-full pl-12 pr-4 py-3 bg-zinc-900 border border-zinc-800 text-zinc-100 focus:border-amber-600 outline-none"
        />
      </div>

      {/* Fighter List */}
      {filteredFighters.length === 0 ? (
        <div className="border border-amber-900/30 bg-zinc-950 p-8 text-center">
          <UserPlus size={48} className="text-amber-700/50 mx-auto mb-4" />
          <h3 className="font-cinzel text-lg text-zinc-300 mb-2">
            {searchTerm ? "No Gladiators Found" : "No Available Gladiators"}
          </h3>
          <p className="text-sm text-zinc-500">
            {searchTerm 
              ? "Try a different search term" 
              : "All gladiators are currently under contract or there are no gladiators in this season yet"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredFighters.map((fighter) => (
            <div
              key={fighter.id}
              className="border border-amber-900/30 bg-zinc-950 p-5 hover:border-amber-600/50 transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-cinzel text-lg text-amber-300 tracking-wide mb-1">
                    {fighter.name}
                  </h3>
                  <p className="text-xs text-zinc-500 capitalize mb-1">{fighter.cls}</p>
                  <p className="text-xs text-zinc-600">by {fighter.user_name}</p>
                </div>
                {fighter.corona_holder && (
                  <Trophy size={20} className="text-amber-400" />
                )}
              </div>

              <div className="flex items-center gap-4 text-xs text-zinc-400 mb-3 pb-3 border-b border-zinc-800">
                <span className="flex items-center gap-1">
                  <Trophy size={12} />
                  {fighter.record.wins}W / {fighter.record.losses}L
                </span>
                <span className="text-zinc-600">|</span>
                <span>{fighter.record.bouts} Bouts</span>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => navigate(`/arena-circuit/fighter/${fighter.id}`)}
                  className={btn() + " flex-1 text-sm bg-zinc-800 hover:bg-zinc-700"}
                >
                  View Profile
                </button>
                <button
                  onClick={() => openOfferModal(fighter)}
                  className={btn() + " flex-1 text-sm"}
                >
                  <Coins size={14} className="inline mr-1" />
                  Offer Contract
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Contract Offer Modal */}
      {offerModal && selectedFighter && (
        <Modal open onClose={() => setOfferModal(false)} title="Offer Contract">
          <div className="space-y-4">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded p-4">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Fighter</div>
              <div className="text-amber-300 font-cinzel text-lg">{selectedFighter.name}</div>
              <div className="text-xs text-zinc-500 capitalize mt-1">{selectedFighter.cls}</div>
            </div>

            <div>
              <label className="text-sm uppercase tracking-widest text-zinc-500 mb-2 block">
                Number of Fights (1-7)
              </label>
              <input
                type="number"
                min={1}
                max={7}
                value={numFights}
                onChange={(e) => setNumFights(Math.max(1, Math.min(7, parseInt(e.target.value) || 1)))}
                className="w-full bg-zinc-900 border border-zinc-800 px-4 py-2 text-zinc-100 focus:border-amber-600 outline-none"
              />
            </div>

            <div>
              <label className="text-sm uppercase tracking-widest text-zinc-500 mb-2 block">
                Payment Per Fight (gold)
              </label>
              <input
                type="number"
                min={1}
                value={paymentPerFight}
                onChange={(e) => setPaymentPerFight(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-full bg-zinc-900 border border-zinc-800 px-4 py-2 text-zinc-100 focus:border-amber-600 outline-none"
              />
            </div>

            <div className="bg-amber-950/20 border border-amber-700/40 rounded p-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-zinc-300">Total Payment (Upfront):</span>
                <span className="text-lg font-cinzel text-amber-300 flex items-center gap-2">
                  <Coins size={18} />
                  {totalPayment} gold
                </span>
              </div>
              <p className="text-xs text-zinc-500 mt-2">
                This amount will be escrowed until the fighter accepts or declines
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setOfferModal(false)}
                className={btn() + " flex-1 bg-zinc-800 hover:bg-zinc-700"}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateOffer}
                className={btn() + " flex-1"}
                disabled={busy}
              >
                <Coins size={16} className="mr-2" />
                {busy ? "Sending..." : "Send Offer"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
